/**
 * @file       Permission service for logged in user
 * @version    2.0.0
 * @author     Mehran Majidi
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or 
 *             altered in anyway without expressed written permission from 
 *             MindShare HDV LLC.
 */
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, catchError, map, of} from 'rxjs';
import { Permission } from '@app/secure/interface/Permission.interface';
import { USER_API } from '@app/constants/Api.constant';
import { ApiService } from '@app/secure/service/Api.service';
import { AuthService } from '@app/service/auth.service';

@Injectable
({
    providedIn: 'root'
})
class PermissionsService 
{
    public permissions$ = new BehaviorSubject <Permission[]>([]);
    constructor
    (
        private apiService: ApiService,
        private injector: Injector
    )
    {
    }

    private isAuthSessionActive
    (
    ): boolean
    {
        let auth: AuthService | null;
        let result: boolean;

        auth = null;
        result = false;
        try
        {
            auth = this.injector.get(AuthService);
        }
        catch (e)
        {
            auth = null;
        }

        if (auth && auth.userValue)
        {
            result = true;
        }

        return result;
    }
    
    async loadPermissions
    (
    )
    {
        let serviceObj: any;
        let postData: any;
        let permissions: Permission[];
        

        try
        {
            postData = {};

            serviceObj = 
                this.apiService.post<any>(USER_API.GET_PERMISSIONS, postData)
            .pipe(map((response: any) =>
            {
                permissions =
                    response?.permissions ||
                    response?.cyData?.permissions ||
                    [];

                if (!this.isAuthSessionActive())
                {
                    return response;
                }

                if (Array.isArray(permissions))
                {
                    this.setPermissions(permissions);
                }
                else
                {
                    throw new Error(
                        'Invalid permissions payload'
                    );
                }

                return response;
            }),
            catchError((error: any) =>
            {
                console.log(error);

                return of(null);
            }));

            serviceObj.subscribe();
        }
        catch(e)
        {
            console.log(e);
        }
    }
    
    setPermissions
    (
        permissions: Permission[]
    )
    {
        this.permissions$.next(permissions);
        try
        {
            if (!permissions || permissions.length === 0)
            {
                localStorage.removeItem('permissions');
            }
            else
            {
                localStorage.setItem(
                    'permissions',
                    JSON.stringify(permissions)
                );
            }
        }
        catch (error)
        {
            console.error('setPermissions localStorage:', error);
        }
    }

    /**
     * @desc   - clear in-memory permissions after logout
     * @access - public
     * @param  -
     * @return -
     */
    clearPersistedStateOnLogout
    (
    )
    {
        this.permissions$.next([]);
        try
        {
            localStorage.removeItem('permissions');
        }
        catch (error)
        {
            console.error('clearPersistedStateOnLogout:', error);
        }
    }
    
    getPermissions
    (
    )
    {
        let fromMemory: Permission[];
        let permissionsJStr: string | null;
        let parsed: any;
        let result: Permission[];

        fromMemory = this.permissions$.value;
        permissionsJStr = null;
        parsed = null;
        result = [];

        if (fromMemory && fromMemory.length > 0)
        {
            result = fromMemory;
        }
        else
        {
            try
            {
                permissionsJStr = localStorage.getItem('permissions');
                if (permissionsJStr)
                {
                    parsed = JSON.parse(permissionsJStr);
                    if (Array.isArray(parsed))
                    {
                        result = parsed;
                        this.permissions$.next(result);
                    }
                }
            }
            catch (error)
            {
                console.error('getPermissions parse error:', error);
                result = [];
            }
        }

        return result;
    }
    
    hasPermission
    (
        moduleName
    )
    {
        let i;
        let found;
        let permissions;
     
        found = false;
        permissions = this.getPermissions();
        
        for (i=0;i<permissions.length;i++)
        {
            if (permissions[i].moduleName == moduleName)
            {
                found = true;
                break;
            }
        }
        
        return found;
    }

    getCaseMotionsPermissions(): string 
    {
        let permissions;
        
        permissions = this.getPermissions();
    
        if (permissions.some(item => 
            item.moduleName === 'Case Motion' &&
            item.actions.split(',').map((a: string) => 
                a.trim()).includes('FULL_ACCESS')
        )) 
        {
            return 'FULL_ACCESS';
        }
    
        if (permissions.some(item => 
            item.moduleName === 'Case Motion' &&
            item.actions.split(',').map((a: string) => 
                a.trim()).includes('READ_ONLY')
        )) 
        {
            return 'READ_ONLY';
        }
    
        return '';
    }
    
    getProbationsPermissions(): string 
    {
        let permissions;
        
        permissions = this.getPermissions();
    
        if (permissions.some(item => 
            item.moduleName === 'Probation System' &&
            item.actions.split(',').map((a: string) => 
                a.trim()).includes('FULL_ACCESS')
        )) 
        {
            return 'FULL_ACCESS';
        }
    
        if (permissions.some(item => 
            item.moduleName === 'Probation System' &&
            item.actions.split(',').map((a: string) => 
                a.trim()).includes('READ_ONLY')
        )) 
        {
            return 'READ_ONLY';
        }
    
        return '';
    }
}

export { PermissionsService }
